import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { keepPreviousPageOf } from "../keepPreviousPage";
import {
  buyPortfolioStock,
  createPortfolioFolder,
  deletePortfolioFolder,
  depositDemoCash,
  followPortfolio,
  getFollowedPortfolios,
  getGalleryTrades,
  getPortfolioFollowState,
  getPortfolioPerformance,
  savePortfolioHoldings,
  unfollowPortfolio,
  getDemoAccount,
  getDemoTrades,
  getPortfolioAnalytics,
  getPortfolioFolders,
  getPortfolioGallery,
  getPortfolioGalleryPreview,
  getPortfolioStocks,
  getPublicPortfolio,
  getPortfolioFolderById,
  resetDemoAccount,
  sellPortfolioStock,
  updatePortfolioAccess,
  updatePortfolioFolder,
} from "./portfolio.api";
import type {
  CreatePortfolioFolderRequest,
  DepositDemoCashRequest,
  GalleryTradesQuery,
  GetDemoTradesQuery,
  PortfolioFollowStateResponse,
  PortfolioPerformanceRange,
  SavePortfolioHoldingsRequest,
  GetPortfolioFoldersQuery,
  GetPortfolioGalleryPreviewQuery,
  GetPortfolioGalleryQuery,
  GetPortfolioStocksQuery,
  PlacePortfolioTradeRequest,
  UpdatePortfolioAccessRequest,
  UpdatePortfolioFolderRequest,
  GetPublicPortfolioQuery,
} from "./portfolio.type";
import { financialIntegrationKeys } from "./financial-integration.hooks";

export const portfolioKeys = {
  all: ["portfolio"] as const,
  folders: () => [...portfolioKeys.all, "folders"] as const,
  folderList: (query: GetPortfolioFoldersQuery) =>
    [...portfolioKeys.folders(), query] as const,
  stocks: () => [...portfolioKeys.all, "stocks"] as const,
  stockList: (query: GetPortfolioStocksQuery) =>
    [...portfolioKeys.stocks(), query] as const,
  analytics: (portfolioId: string) =>
    [...portfolioKeys.all, "analytics", portfolioId] as const,
  account: (portfolioId: string) =>
    [...portfolioKeys.all, "account", portfolioId] as const,
  trades: (portfolioId: string, query: GetDemoTradesQuery) =>
    [...portfolioKeys.all, "trades", portfolioId, query] as const,
  gallery: () => [...portfolioKeys.all, "gallery"] as const,
  galleryList: (query: GetPortfolioGalleryQuery) =>
    [...portfolioKeys.gallery(), "list", query] as const,
  galleryPreview: (id: string, query?: GetPortfolioGalleryPreviewQuery) =>
    [...portfolioKeys.gallery(), "preview", id, query] as const,
  folderDetail: (id: string) =>
    [...portfolioKeys.folders(), "detail", id] as const,
  followState: (id: string) =>
    [...portfolioKeys.all, "follow", id] as const,
  followed: () => [...portfolioKeys.all, "followed"] as const,
  galleryTrades: (id: string, query: GalleryTradesQuery) =>
    [...portfolioKeys.gallery(), "trades", id, query] as const,
  performance: (id: string, range: PortfolioPerformanceRange) =>
    [...portfolioKeys.gallery(), "performance", id, range] as const,
};

export const usePortfolioFolders = (
  query: GetPortfolioFoldersQuery = {},
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: portfolioKeys.folderList(query),
    queryFn: () => getPortfolioFolders(query),
    enabled: options?.enabled !== false,
  });
};

export const usePortfolioStocks = (
  query: GetPortfolioStocksQuery,
  enabled = true
) => {
  return useQuery({
    queryKey: portfolioKeys.stockList(query),
    queryFn: () => getPortfolioStocks(query),
    // Keep the rows while another page of the same folder loads.
    placeholderData: (previous, previousQuery) => {
      const prev = previousQuery?.queryKey.at(-1) as
        | GetPortfolioStocksQuery
        | undefined;
      return prev?.folder_id === query.folder_id && prev?.userid === query.userid
        ? previous
        : undefined;
    },
    enabled,
  });
};

export const useCreatePortfolioFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePortfolioFolderRequest) =>
      createPortfolioFolder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.folders(),
      });
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.gallery(),
      });
    },
  });
};

export const useUpdatePortfolioFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdatePortfolioFolderRequest) =>
      updatePortfolioFolder(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.folders(),
      });
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.gallery(),
      });
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.galleryPreview(variables.id),
      });
    },
  });
};

export const useDeletePortfolioFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePortfolioFolder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.folders(),
      });
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.gallery(),
      });
    },
  });
};

const invalidateFolderTrading = (
  queryClient: ReturnType<typeof useQueryClient>,
  folderId: string,
) => {
  queryClient.invalidateQueries({ queryKey: portfolioKeys.stocks() });
  queryClient.invalidateQueries({
    queryKey: portfolioKeys.analytics(folderId),
  });
  queryClient.invalidateQueries({
    queryKey: portfolioKeys.account(folderId),
  });
  queryClient.invalidateQueries({
    queryKey: [...portfolioKeys.all, "trades", folderId],
  });
};

export const useBuyPortfolioStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PlacePortfolioTradeRequest) =>
      buyPortfolioStock(payload),
    onSuccess: (_, variables) => {
      invalidateFolderTrading(queryClient, variables.folder_id);
    },
  });
};

export const useSellPortfolioStock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PlacePortfolioTradeRequest) =>
      sellPortfolioStock(payload),
    onSuccess: (_, variables) => {
      invalidateFolderTrading(queryClient, variables.folder_id);
    },
  });
};

export const useDemoAccount = (
  folderId: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: portfolioKeys.account(folderId || ""),
    queryFn: () => getDemoAccount(folderId!),
    enabled: (options?.enabled ?? true) && Boolean(folderId),
  });
};

export const useResetDemoAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (folderId: string) => resetDemoAccount(folderId),
    onSuccess: (_, folderId) => {
      invalidateFolderTrading(queryClient, folderId);
    },
  });
};

export const useDemoTrades = (
  folderId: string | undefined,
  query: GetDemoTradesQuery = {},
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: portfolioKeys.trades(folderId || "", query),
    queryFn: () => getDemoTrades(folderId!, query),
    placeholderData: keepPreviousPageOf(folderId),
    enabled: (options?.enabled ?? true) && Boolean(folderId),
  });
};

export const usePortfolioAnalytics = (
  portfolioId: string | undefined,
  options?: { enabled?: boolean; retry?: boolean | number },
) => {
  return useQuery({
    queryKey: portfolioKeys.analytics(portfolioId || ""),
    queryFn: () => getPortfolioAnalytics(portfolioId!),
    enabled: (options?.enabled ?? true) && Boolean(portfolioId),
    retry: options?.retry,
  });
};

export const useUpdatePortfolioAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdatePortfolioAccessRequest) =>
      updatePortfolioAccess(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.folders(),
      });
      queryClient.invalidateQueries({
        queryKey: financialIntegrationKeys.snaptradeAccounts(),
      });
    },
  });
};

export const usePortfolioGallery = (
  query: GetPortfolioGalleryQuery = {},
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: portfolioKeys.galleryList(query),
    queryFn: () => getPortfolioGallery(query),
    placeholderData: keepPreviousData,
    enabled: options?.enabled !== false,
  });
};

export const usePortfolioGalleryPreview = (
  id: string | undefined,
  options?: { enabled?: boolean } & GetPortfolioGalleryPreviewQuery
) => {
  const query = { page: options?.page, limit: options?.limit };

  return useQuery({
    queryKey: portfolioKeys.galleryPreview(id || "", query),
    queryFn: () => getPortfolioGalleryPreview(id!, query),
    placeholderData: keepPreviousPageOf(id),
    enabled: Boolean(id) && options?.enabled !== false,
  });
};

export const usePortfolioFolder = (
  id: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: portfolioKeys.folderDetail(id || ""),
    queryFn: () => getPortfolioFolderById(id!),
    enabled: Boolean(id) && options?.enabled !== false,
  });
};

export const usePublicPortfolio = (
  hash: string | undefined,
  query?: GetPublicPortfolioQuery
) => {
  return useQuery({
    queryKey: ["public-portfolio", hash, query],
    queryFn: () => getPublicPortfolio(hash!, query),
    placeholderData: keepPreviousPageOf(hash),
    enabled: !!hash,
  });
};

// ---- Ready-made portfolios (staff only) ----

interface SaveHoldingsVariables {
  folderId: string;
  body: SavePortfolioHoldingsRequest;
}

/** Dry run: the exact trades Save would make, at live prices. Nothing is written. */
export const usePreviewPortfolioHoldings = () => {
  return useMutation({
    mutationFn: ({ folderId, body }: SaveHoldingsVariables) =>
      savePortfolioHoldings(folderId, body, { preview: true }),
  });
};

export const useSavePortfolioHoldings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, body }: SaveHoldingsVariables) =>
      savePortfolioHoldings(folderId, body),
    onSuccess: (_, variables) => {
      invalidateFolderTrading(queryClient, variables.folderId);
      queryClient.invalidateQueries({ queryKey: portfolioKeys.folders() });
      queryClient.invalidateQueries({ queryKey: portfolioKeys.gallery() });
      queryClient.invalidateQueries({
        queryKey: portfolioKeys.folderDetail(variables.folderId),
      });
    },
  });
};

export const useDepositDemoCash = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DepositDemoCashRequest) => depositDemoCash(payload),
    onSuccess: (_, variables) => {
      invalidateFolderTrading(queryClient, variables.folder_id);
    },
  });
};

// ---- Ready-made portfolios: follow (free) and public trade history ----

/** Follow state + follower count for one ready-made portfolio. */
export const usePortfolioFollowState = (
  id: string | undefined,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: portfolioKeys.followState(id || ""),
    queryFn: () => getPortfolioFollowState(id!),
    enabled: Boolean(id) && options?.enabled !== false,
  });

/** Ids of the ready-made portfolios the viewer follows (one request for a whole gallery page). */
export const useFollowedPortfolioIds = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: portfolioKeys.followed(),
    queryFn: () => getFollowedPortfolios({ page: 1, limit: 100 }),
    enabled: options?.enabled !== false,
    select: (response) => new Set((response.data ?? []).map((folder) => folder.id)),
  });

interface ToggleFollowVariables {
  id: string;
  follow: boolean;
}

/** Follow / unfollow with an optimistic follow state, rolled back on error. */
export const useToggleFollowPortfolio = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, follow }: ToggleFollowVariables) =>
      follow ? followPortfolio(id) : unfollowPortfolio(id),
    onMutate: async ({ id, follow }) => {
      const key = portfolioKeys.followState(id);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PortfolioFollowStateResponse>(key);
      if (previous?.data) {
        const delta = follow === previous.data.following ? 0 : follow ? 1 : -1;
        queryClient.setQueryData<PortfolioFollowStateResponse>(key, {
          ...previous,
          data: {
            following: follow,
            follower_count: Math.max(0, previous.data.follower_count + delta),
          },
        });
      }
      return { previous };
    },
    onError: (_error, { id }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(portfolioKeys.followState(id), context.previous);
      }
    },
    onSuccess: (response, { id }) => {
      queryClient.setQueryData(portfolioKeys.followState(id), response);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: portfolioKeys.followed() });
      queryClient.invalidateQueries({ queryKey: portfolioKeys.gallery() });
    },
  });
};

export const useGalleryTrades = (
  id: string | undefined,
  query: GalleryTradesQuery,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: portfolioKeys.galleryTrades(id || "", query),
    queryFn: () => getGalleryTrades(id!, query),
    enabled: Boolean(id) && options?.enabled !== false,
    placeholderData: (previous) => previous,
  });

export const usePortfolioPerformance = (
  id: string | undefined,
  range: PortfolioPerformanceRange,
  options?: { enabled?: boolean },
) =>
  useQuery({
    queryKey: portfolioKeys.performance(id || "", range),
    queryFn: () => getPortfolioPerformance(id!, range),
    enabled: Boolean(id) && options?.enabled !== false,
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });
